import { http, HttpResponse } from "msw";

import { makeCepResponse, makeCnpjResponse } from "./cep-cnpj.factory";
import { fakeClientsList, fakeCreateClientPayload } from "./faker";
import { ClientDetail, ListClientsResponse } from "../api/client";

// -------------------------------------------------------
// Store interno com shape completo (ClientDetail + deletedAt)
// -------------------------------------------------------
type ClientRecord = ClientDetail & { deletedAt?: string };

const clientsStore: ClientRecord[] = fakeClientsList(20).map((c) => ({
  ...c,
  ...fakeCreateClientPayload(),
}));

// -------------------------------------------------------
// Helpers
// -------------------------------------------------------
const toListResponse = (client: ClientRecord): ListClientsResponse => ({
  id: client.id,
  cnpj: client.cnpj,
  name: client.name,
  fantasyName: client.fantasyName,
  responsibleName: client.responsible?.name,
});

const applyFilters = (
  clients: ClientRecord[],
  params: URLSearchParams,
): ClientRecord[] => {
  // Excluir registros com soft delete
  let result = clients.filter((c) => !c.deletedAt);

  const name = params.get("name");
  const fantasyName = params.get("fantasyName");
  const cnpj = params.get("cnpj");
  const responsibleName = params.get("responsibleName");
  const sortBy = params.get("sortBy") as keyof ClientDetail | null;
  const sortOrder = params.get("sortOrder") ?? "asc";

  if (name)
    result = result.filter((c) =>
      c.name.toLowerCase().includes(name.toLowerCase()),
    );
  if (fantasyName)
    result = result.filter((c) =>
      c.fantasyName.toLowerCase().includes(fantasyName.toLowerCase()),
    );
  if (cnpj)
    result = result.filter((c) =>
      c.cnpj.toLowerCase().includes(cnpj.toLowerCase()),
    );
  if (responsibleName)
    result = result.filter((c) =>
      c.responsible?.name.toLowerCase().includes(responsibleName.toLowerCase()),
    );

  if (sortBy) {
    result.sort((a, b) => {
      const valA = String(a[sortBy] ?? "");
      const valB = String(b[sortBy] ?? "");
      return sortOrder === "desc"
        ? valB.localeCompare(valA)
        : valA.localeCompare(valB);
    });
  }

  return result;
};

const paginate = <T>(items: T[], page: number, limit: number) => ({
  data: items.slice((page - 1) * limit, page * limit),
  meta: {
    currentPage: page,
    itemsPerPage: limit,
    totalItems: items.length,
    totalPages: Math.ceil(items.length / limit),
    hasNextPage: page * limit < items.length,
    hasPreviousPage: page > 1,
  },
});

// -------------------------------------------------------
// Handlers
// -------------------------------------------------------
const API_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:3001";
const BASE = `${API_URL}/v1/managerial/tenants`;

const tenantHandlers = [
  // GET /v1/managerial/tenants — listagem paginada com filtros
  http.get(`${BASE}`, ({ request }) => {
    const params = new URL(request.url).searchParams;
    const page = Math.max(1, Number(params.get("page") ?? 1));
    const limit = Math.min(100, Math.max(1, Number(params.get("limit") ?? 10)));

    const filtered = applyFilters(clientsStore, params);
    const paged = paginate(filtered.map(toListResponse), page, limit);

    return HttpResponse.json(paged, { status: 200 });
  }),

  // GET /v1/managerial/tenants/:id — cliente completo
  http.get(`${BASE}/:id`, ({ params }) => {
    const { id } = params;
    const client = clientsStore.find((c) => c.id === id && !c.deletedAt);

    if (!client) {
      return HttpResponse.json(
        { message: "Cliente não encontrado" },
        { status: 404 },
      );
    }

    return HttpResponse.json(client, { status: 200 });
  }),

  // POST /v1/managerial/tenants — cria cliente, retorna { id }
  http.post(BASE, async ({ request }) => {
    const body = (await request.json()) as Omit<ClientDetail, "id">;
    const newClient: ClientDetail = { ...body, id: crypto.randomUUID() };
    clientsStore.push(newClient);

    return HttpResponse.json({ id: newClient.id }, { status: 201 });
  }),

  // PUT /v1/managerial/tenants/:id — edita cliente, retorna ClientDetail
  http.put(`${BASE}/:id`, async ({ request, params }) => {
    const { id } = params;
    const body = (await request.json()) as Omit<ClientDetail, "id">;
    const index = clientsStore.findIndex((c) => c.id === id && !c.deletedAt);

    if (index === -1) {
      return HttpResponse.json(
        { message: "Cliente não encontrado" },
        { status: 404 },
      );
    }

    clientsStore[index] = { ...clientsStore[index], ...body };

    return HttpResponse.json(clientsStore[index], { status: 200 });
  }),

  // DELETE /v1/managerial/tenants/:id — soft delete
  http.delete(`${BASE}/:id`, ({ params }) => {
    const { id } = params;
    const index = clientsStore.findIndex((c) => c.id === id);

    if (index === -1) {
      return HttpResponse.json(
        { message: "Cliente não encontrado" },
        { status: 404 },
      );
    }

    // Soft delete: marca o registro com deletedAt sem removê-lo do store
    clientsStore[index] = {
      ...clientsStore[index],
      deletedAt: new Date().toISOString(),
    };

    return new HttpResponse(null, { status: 204 });
  }),

  // PATCH /v1/managerial/tenants/:id/logo — upload de logo
  http.patch(`${BASE}/:id/logo`, async ({ params }) => {
    const { id } = params;
    const index = clientsStore.findIndex((c) => c.id === id);

    if (index === -1) {
      return HttpResponse.json(
        { message: "Cliente não encontrado" },
        { status: 404 },
      );
    }

    clientsStore[index] = {
      ...clientsStore[index],
      logo: `https://placeholder.com/logo-${id}.png`,
    };

    return new HttpResponse(null, { status: 204 });
  }),
];

export const clientsHandlers = [
  http.get("https://viacep.com.br/ws/:cep/json/", ({ params }) => {
    return HttpResponse.json(makeCepResponse(params.cep as string));
  }),

  http.get("https://brasilapi.com.br/api/cnpj/v1/:cnpj", ({ params }) => {
    return HttpResponse.json(makeCnpjResponse(params.cnpj as string));
  }),

  ...tenantHandlers,
];
